import { createContext, ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";

export type TaskFilter = {
  field: string;
  operator: string;
  value: any;
};

export type TaskSort = {
  field: string;
  descending?: boolean;
};

type contextType = {
  upsertFilter: (f: TaskFilter) => void;
  reset: () => void;
  filters: TaskFilter[];
  getFilters: () => TaskFilter[];
  sorts: TaskSort[];
  updateSorts: (s: TaskSort[]) => void;
};
const defaultFilters = (
  queryParams: URLSearchParams,
  initialStages: string[] | undefined,
  initialFamilies: string[] | undefined,
  initialPriorities: string[] | undefined,
  initialProjects: string[] | undefined,
  initialTimeRange: string | undefined,
): TaskFilter[] => {
  const familyParam = queryParams.get("familyRefs");
  const familyRefs: string[] | undefined = familyParam
    ? familyParam.split(",")
    : undefined;
  const filters = [];
  if (familyRefs) {
    filters.push({ field: "family", operator: "oneOf", value: familyRefs });
  }
  if (initialStages) {
    filters.push({ field: "stage", operator: "oneOf", value: initialStages });
  }
  if (initialFamilies) {
    filters.push({
      field: "family",
      operator: "oneOf",
      value: initialFamilies,
    });
  }
  if (initialPriorities) {
    filters.push({
      field: "priority",
      operator: "oneOf",
      value: initialPriorities,
    });
  }
  if (initialProjects) {
    filters.push({
      field: "project",
      operator: "oneOf",
      value: initialProjects,
    });
  }
  if (initialTimeRange) {
    filters.push({
      field: "timeRange",
      operator: "equals",
      value: initialTimeRange,
    });
  }
  return filters;
};

export const FilterContext = createContext<contextType>({
  upsertFilter: () => {},
  reset: () => {},
  filters: [],
  getFilters: () => [],
  sorts: [],
  updateSorts: () => {},
});

export const AddFilterContext = ({
  children,
  initialStages,
  initialFamilies,
  initialPriorities,
  initialProjects,
  initialTimeRange,
  onFilterChange,
}: {
  children: ReactNode;
  initialStages: string[] | undefined;
  initialFamilies?: string[] | undefined;
  initialPriorities?: string[] | undefined;
  initialProjects?: string[] | undefined;
  initialTimeRange?: string | undefined;
  onFilterChange?: (filters: TaskFilter[]) => void;
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState<TaskFilter[]>(
    defaultFilters(
      queryParams,
      initialStages,
      initialFamilies,
      initialPriorities,
      initialProjects,
      initialTimeRange,
    ),
  );
  const [sorts, setSorts] = useState<TaskSort[]>([]);
  const upsertFilter = (f: TaskFilter) => {
    const newFilters = filters.filter((filter) => filter.field !== f.field);
    newFilters.push(f);
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };
  return (
    <FilterContext.Provider
      value={{
        filters,
        upsertFilter,
        reset: () => setFilters([]),
        getFilters: () => filters,
        sorts,
        updateSorts: (newSorts: TaskSort[]) => {
          setSorts(newSorts);
        },
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};
